import { IMicrophoneGainSetting, IMicrophone } from '@/types/components';
import {
  MIC_INITIAL_GAIN,
  MIC_MAGNIFICATION_GAIN,
} from '@/utils/constants/microphone';
import { classifyDeviceType } from '@/utils/device';

interface IUniqueDevice {
  id: string;
  label: string;
  default: boolean;
  groupId: string;
}

// 마이크 기기 목록을 포맷팅합니다.
export default async function formatMicrophones(
  devices: MediaDeviceInfo[],
  gainSettings: IMicrophoneGainSetting
): IMicrophone[] {
  // 01) 중복 제거
  const uniqueDevices = getUniqueDevices(devices);

  // 02) 기본기기설정
  const devicesWithDefault = ensureDefaultDevice(uniqueDevices);

  return formatDevices(devicesWithDefault, gainSettings);
}

/**
 * 장치별 게인 설정을 가져옵니다.
 */
const getDeviceGains = (
  label: string,
  gainSettings: IMicrophoneGainSetting
) => {
  const formatedLabel = label.toLowerCase();

  // 초기게인
  const { gain: initialGain = MIC_INITIAL_GAIN } =
    gainSettings.initialGains.find((setting) => {
      return formatedLabel.includes(setting.micLabel);
    }) || {};

  // 게인배율
  const { gain: magnificationGain = MIC_MAGNIFICATION_GAIN } =
    gainSettings.magnificationGains.find((setting) => {
      return formatedLabel.includes(setting.micLabel) || setting.mode;
    }) || {};

  return {
    initialGain,
    magnificationGain,
  };
};

// 기본기기를 추출합니다.
const extractDefaultDevice = (devices: MediaDeviceInfo[]) => {
  const defaultDevice = devices.find(
    (d) => d.deviceId === 'default' && !/\bvirtual\b/i.test(d.label)
  );

  return defaultDevice
    ? {
        groupId: defaultDevice.groupId,
        label: defaultDevice.label.replace(/^.*? - /, ''),
      }
    : {
        groupId: '',
        label: '',
      };
};

/**
 * 안드로이드 환경, USB 마이크 우선 여부
 */
const isAndroidUSBFilter = (
  devices: MediaDeviceInfo[],
  label: string
): boolean => {
  return (
    /android/i.test(navigator.userAgent.toLowerCase()) &&
    devices.some((d) => d.label.toLowerCase().includes('usb')) &&
    (label.toLowerCase().includes('headset earpiece') ||
      label.toLowerCase().includes('speakerphone'))
  );
};

// 기기 중복 제거
function getUniqueDevices(devices: MediaDeviceInfo[]): IUniqueDevice[] {
  const deviceType = classifyDeviceType();
  const defaultDevice = extractDefaultDevice(devices);
  const uniqueKeys = new Set<string>();

  return devices.reduce<IUniqueDevice[]>(
    (acc, { deviceId, label, groupId }) => {
      if (
        !label ||
        ['communications', 'default'].includes(deviceId) ||
        isAndroidUSBFilter(devices, label)
      ) {
        return acc;
      }

      const key = deviceType === 'm' ? groupId : deviceId;
      if (!uniqueKeys.has(key)) {
        uniqueKeys.add(key);
        acc.push({
          id: deviceId,
          label,
          groupId,
          default:
            defaultDevice.groupId === groupId && defaultDevice.label === label,
        });
      }
      return acc;
    },
    []
  );
}

/**
 * 기본 기기가 없을 경우, 아래 규칙에 따라 기본 기기를 지정합니다.
 * 1순위) 라벨에 virtual 혹은 blackhole이 포함될 경우
 * 2순위) 첫 번째 기기
 */
const ensureDefaultDevice = (
  devices: IUniqueDevice[] = []
): IUniqueDevice[] => {
  if (devices.length === 0 || devices.some((d) => d.default)) {
    return devices;
  }

  const formattedDevices = [...devices];

  const labelInx = formattedDevices.findIndex(
    (d) => !/\bvirtual\b/i.test(d.label) && !/blackhole/i.test(d.label)
  );
  const inx = labelInx > -1 ? labelInx : 0;

  formattedDevices[inx] = { ...formattedDevices[inx], default: true };
  return formattedDevices;
};

// 게인값 주입 및 정렬
const formatDevices = (
  devices: IUniqueDevice[],
  gainSettings: IMicrophoneGainSetting
): IMicrophone[] => {
  return devices
    .map((device) => ({
      ...device,
      ...getDeviceGains(device.label, gainSettings),
      customLabel: '',
      isConnected: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
